import type { Crepe } from '@milkdown/crepe';
import type { useNodeViewFactory, usePluginViewFactory } from '@prosemirror-adapter/react';

import { embedPlugin } from './embed';
import { configureEmbedNodeView } from './node-view';
import { embedTooltip, EmbedTooltipView } from './tooltip';
import { createSlashMenuItemForProvider, type SlashMenuItemConfig } from './slash-items';
// Import providers array directly - ensures bundler includes all provider modules
import { providers } from './providers';

/** Minimal interface for slash menu group returned by builder.addGroup() */
interface SlashMenuGroup {
  addItem: (key: string, item: SlashMenuItemConfig) => SlashMenuGroup;
}

export interface ConfigureEmbedsOptions {
  /** Node view factory from useNodeViewFactory() */
  nodeViewFactory: ReturnType<typeof useNodeViewFactory>;
  /** Plugin view factory from usePluginViewFactory() */
  pluginViewFactory: ReturnType<typeof usePluginViewFactory>;
}

/**
 * Configure embed blocks feature for the editor.
 * Installs all embed plugins, NodeView, and tooltip.
 *
 * Usage:
 * ```ts
 * const nodeViewFactory = useNodeViewFactory();
 * const pluginViewFactory = usePluginViewFactory();
 *
 * // In useEditor callback:
 * configureEmbeds(crepe, { nodeViewFactory, pluginViewFactory });
 * ```
 */
export function configureEmbeds(crepe: Crepe, { nodeViewFactory, pluginViewFactory }: ConfigureEmbedsOptions): void {
  // Register embed plugins (remark transform, node schema, input rule)
  embedPlugin.forEach((plugin) => crepe.editor.use(plugin));

  // Register NodeView for rendering embeds
  crepe.editor.config((ctx) => configureEmbedNodeView(ctx, nodeViewFactory));

  // Register tooltip for URL input
  crepe.editor.config((ctx) => {
    ctx.set(embedTooltip.key, {
      view: pluginViewFactory({ component: EmbedTooltipView }),
    });
  });
  crepe.editor.use(embedTooltip);
}

/**
 * Adds embed items to a slash menu group.
 * Call this in Crepe's buildMenu callback.
 *
 * Usage:
 * ```ts
 * [Crepe.Feature.BlockEdit]: {
 *   buildMenu: (builder) => {
 *     const embedsGroup = builder.addGroup('embeds', 'Embeds');
 *     configureEmbedSlashMenu(embedsGroup);
 *   },
 * },
 * ```
 */
export function configureEmbedSlashMenu(group: SlashMenuGroup): void {
  // Use providers array directly (ensures bundler includes all provider modules)
  for (const provider of providers) {
    const slashItem = createSlashMenuItemForProvider(provider);
    group.addItem(provider.id, slashItem());
  }
}
